import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Grid, Dropdown } from 'semantic-ui-react';
import Button from '../../../../components/Button';
import { iRootDispatch, iRootState } from '../../../../store';
import Input from '../../../../components/Input';
import { FieldLabel } from '../../../../styles';
import { AvailableBrokersAndEstatesState } from '../../../../store/types/temp-types';
import { dangerNotification } from '../../../../services/notification';
import { EstatePreregisterListPageProps } from '../..';
import { ButtonsContainer } from '../../styles';
import { maskCpfOrCnpj } from '../../../../services/masks';
import EstatePreRegisterListPageTable from '../Table';
import { useQuery } from '@tanstack/react-query';
import ViewModal from '../ViewModal';
import { EstatePreregisterState } from '../../../../types/EstatePreregisterState';
import { getEstatesPreregisterList } from '../../../../services/estate';
import { statusOptions, typeOptions } from '../../types';
import { hasPermission } from '../../../../components/AuthorizationContainer';
import { ActionEnum } from '../../../../enums/authz-action.enum';
import { FeatureEnum } from '../../../../enums/authz-feature.enum';

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

const w100 = {
  width: '100%'
};

interface EstatesListFilter {
  document: string;
  name: string;
  address: string;
}

const EstatePregisterListPage = (props: EstatePreregisterListPageProps) => {
  const { user, availableBrokersAndEstates } = props;
  const [filterContent, setFilterContent] = useState<EstatesListFilter>({
    document: '',
    name: '',
    address: ''
  });
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedFranchise, setSelectedFranchise] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [cleanInputs, setCleanInputs] = useState(false);
  const [type, setType] = useState<string | null>('document');
  const [argument, setArgument] = useState<string>('');
  const [isSearchEnabled, setSearchEnabled] = useState(true);
  const [itemSelected, setItemSelected] = useState<EstatePreregisterState | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [userCanDetail, setUserCanDetail] = useState<boolean>(false);

  const [total, setTotal] = useState(0);

  const franchiseOptions = availableBrokersAndEstates.franchises;

  useEffect(() => {
    const has = hasPermission(ActionEnum.detail, FeatureEnum.preregisters);
    setUserCanDetail(has);
  }, []);

  const getContentByType = () => {
    if (type === 'document') {
      return filterContent.document;
    }
    if (type === 'address') {
      return filterContent.address;
    }

    return filterContent.name;
  };

  const getData = async () => {
    try {
      const filters: {
        page: string;
        type: string | null;
        argument: string;
        status: string;
        franchise: string;
      } = {
        page: `${currentPage}`,
        type: type ?? null,
        argument: getContentByType(),
        status: selectedStatus,
        franchise: selectedFranchise
      };

      const response = await getEstatesPreregisterList(filters);

      if (currentPage === 1) setTotal(response.count);

      setCurrentPage(response.page);

      return response;
    } catch (e) {
      dangerNotification(
        'Oops...',
        'Não foi possível buscar as imobiliárias pré-cadastradas!'
      );
    } finally {
      setCleanInputs(false);
    }
  };

  const {
    data,
    isFetching: isLoading,
    refetch
  } = useQuery(['getData', currentPage], getData, {
    enabled: true,
    keepPreviousData: true,
    refetchOnMount: 'always'
  });

  useEffect(() => {
    refetch();
  }, [cleanInputs]);

  const clearInputs = () => {
    setFilterContent({
      document: '',
      name: '',
      address: ''
    });
    setType(null);
    setArgument('');
    setSelectedStatus('');
    setSelectedFranchise('');
    setCleanInputs(true);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    switch (name) {
      case 'argument': {
        if (type === 'document') {
          const numericValue = value?.replace(/\D/g, '');
          const maskedValue = maskCpfOrCnpj(numericValue);
          setArgument(maskedValue);
          const isValidDocumentSearch =
            numericValue.length === 11 || numericValue.length === 14;
          setSearchEnabled(isValidDocumentSearch);
          break;
        }
        setSearchEnabled(true);
        setArgument(value);
        break;
      }
    }
  };

  useEffect(() => {
    if (type === 'document') {
      setFilterContent({ ...filterContent, document: argument });
    } else if (type === 'address') {
      setFilterContent({ ...filterContent, address: argument });
    } else if (type === 'name') {
      setFilterContent({ ...filterContent, name: argument });
    }
  }, [argument]);

  useEffect(() => {
    setFilterContent({
      name: '',
      document: '',
      address: ''
    });
  }, [type]);

  useEffect(() => {
    cleanInputs && getData();
  }, [cleanInputs]);

  return (
    <>
      <Grid>
        <Grid.Row columns={'equal'} style={{ flexWrap: 'wrap' }}>
          <Grid.Column>
            <FieldLabel>Pesquisar por</FieldLabel>
            <div style={w100}>
              <Dropdown
                style={w100}
                value={type || ''}
                name="type"
                options={typeOptions}
                placeholder={'Selecionar...'}
                search
                selection
                onChange={(e, { value }) => setType(value as string)}
                clearable
              />
            </div>
          </Grid.Column>
          <Grid.Column>
            <FieldLabel>Termo de pesquisa:</FieldLabel>
            <Input
              name="argument"
              onChange={handleInputChange}
              component={Input}
              value={argument}
              fluid
            />
          </Grid.Column>
          <Grid.Column>
            <FieldLabel>Status:</FieldLabel>
            <Dropdown
              style={{
                width: '100%'
              }}
              value={selectedStatus}
              name={`status`}
              options={statusOptions}
              placeholder={'Selecionar...'}
              search
              selection
              onChange={(_, { value }) => {
                setSelectedStatus(value as string);
              }}
              clearable
            />
          </Grid.Column>
          <Grid.Column>
            <FieldLabel>Franquia:</FieldLabel>
            <Dropdown
              style={{
                width: '100%'
              }}
              value={selectedFranchise}
              name={`franchise`}
              options={franchiseOptions}
              placeholder={'Selecionar...'}
              search
              selection
              onChange={(_, { value }) => {
                setSelectedFranchise(value as string);
              }}
              clearable
            />
          </Grid.Column>
          <Grid.Column>
            <ButtonsContainer>
              <Button
                fluid
                type={'submit'}
                disabled={!isSearchEnabled}
                text="Pesquisar"
                onClick={() => {
                  setCurrentPage(1);
                  refetch();
                }}
              />
              <Button light fluid type={'button'} text="Limpar" onClick={clearInputs} />
            </ButtonsContainer>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <EstatePreRegisterListPageTable
        estates={data?.data}
        isLoading={isLoading}
        currentPage={data?.page}
        setCurrentPage={setCurrentPage}
        getData={getData}
        dataCount={total}
        handleSelectItem={(item: EstatePreregisterState) => {
          if (userCanDetail) {
            setItemSelected(item);
            setIsViewModalOpen(true);
          }
        }}
      />
      {itemSelected && (
        <ViewModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          estate={itemSelected}
        />
      )}
    </>
  );
};

export default connect(mapState, mapDispatch)(EstatePregisterListPage);
